import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ClipboardPlus, MessageCircle, Minus, Plus, Check, AlertCircle, ImageOff } from 'lucide-react';
import { getProduct, getCategory, formatMeters, getAllProducts, STORE } from '../data/catalog';
import { useSolicitation } from '../context/SolicitationContext';
import ProductCard from '../components/ProductCard';
import { availableVariants, isProductVisible, isVariantless, useAvailabilityTick } from '../utils/availability';
import { resolveVariantImage, resolveProductImage, isIllustrativeVariantImage, isIllustrativeProductImage } from '../utils/photoStatus';

const QUICK_METERS = [1, 1.5, 2, 2.5, 3];
const MIN_M = 0.01;
const MAX_M = 999.99;
const STEP = 0.1;

// Aceita vírgula ou ponto (3,5 / 3.5 / 3,50 / 3.50) e normaliza internamente.
// Rejeita: 0, negativos, texto inválido, acima de 999,99 e mais de 2 casas decimais.
const parseMeters = (str) => {
  let normalized = String(str).trim().replace(',', '.');
  if (normalized.startsWith('.')) normalized = `0${normalized}`;
  if (normalized.endsWith('.')) normalized = normalized.slice(0, -1);
  if (!/^\d+(\.\d+)?$/.test(normalized)) return null;
  const decimals = normalized.split('.')[1];
  if (decimals && decimals.length > 2) return null;
  const n = parseFloat(normalized);
  if (n < MIN_M || n > MAX_M) return null;
  return Math.round(n * 100) / 100;
};

const formatBr = (n) => n.toFixed(2).replace('.', ',');

function StepTitle({ n, children, testid }) {
  return (
    <p className="flex items-center gap-2.5 text-base font-semibold text-navy" data-testid={testid}>
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-navy text-xs font-bold text-white">
        {n}
      </span>
      {children}
    </p>
  );
}

export default function ProductPage() {
  const { slug } = useParams();
  const product = getProduct(slug);
  const { addItem, addCustomItem } = useSolicitation();
  useAvailabilityTick();
  const variantless = product ? isVariantless(product) : false;
  const avail = product ? availableVariants(product) : [];
  const multi = avail.length > 1;
  const [variantId, setVariantId] = useState(null);
  // Produto sem variantes: metragem começa vazia (nunca adiciona 1 m automaticamente).
  const [inputValue, setInputValue] = useState(variantless ? '' : '1');
  const [customNote, setCustomNote] = useState('');
  const [customMeters, setCustomMeters] = useState('');
  const [customError, setCustomError] = useState('');

  useEffect(() => {
    setVariantId(null);
    const p = getProduct(slug);
    setInputValue(p && isVariantless(p) ? '' : '1');
    setCustomNote('');
    setCustomMeters('');
    setCustomError('');
  }, [slug]);

  if (!product) {
    return (
      <main className="mx-auto max-w-7xl px-4 py-20 text-center">
        <h1 className="font-serif text-3xl text-navy">Tecido não encontrado</h1>
        <Link to="/categoria/todos" className="mt-4 inline-block text-sm font-semibold text-navy underline">
          Ver todos os tecidos
        </Link>
      </main>
    );
  }

  const visible = variantless ? isProductVisible(product) : avail.length > 0;
  if (!visible) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-16 text-center" data-testid="product-unavailable-page">
        <h1 className="font-serif text-3xl text-navy">{product.name}</h1>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          Este tecido está temporariamente indisponível para novas solicitações. Fale com nossa
          equipe para confirmar reposição ou conhecer opções semelhantes.
        </p>
        <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a
            href={`https://wa.me/${STORE.whatsappDigits}?text=${encodeURIComponent(`Olá! Gostaria de saber sobre a disponibilidade do tecido ${product.name}.`)}`}
            target="_blank"
            rel="noreferrer"
            className="rounded-full bg-navy px-7 py-3 text-sm font-semibold text-white hover:bg-navy-hover"
            data-testid="product-unavailable-whatsapp"
          >
            PERGUNTAR NO WHATSAPP
          </a>
          <Link to="/categoria/todos" className="rounded-full border border-navy/20 px-7 py-3 text-sm font-semibold text-navy hover:bg-navy-light" data-testid="product-unavailable-browse">
            EXPLORAR TECIDOS
          </Link>
        </div>
      </main>
    );
  }

  const selected = variantless ? null : multi ? avail.find((v) => v.id === variantId) || null : avail[0];
  const shown = selected || avail[0] || null;
  // Capa comercial (IA): só antes de o cliente selecionar uma variante; depois, sempre a foto REAL.
  const coverShown = !variantless && !!product.coverImage && !selected;
  const meters = parseMeters(inputValue);
  const metersError = inputValue.trim() !== '' && meters === null;
  const canAdd = (variantless || !!selected) && meters !== null;
  const disabledReason = !variantless && !selected
    ? 'Escolha uma cor ou estampa para continuar.'
    : inputValue.trim() === ''
      ? 'Informe a metragem desejada.'
      : meters === null
        ? 'Informe uma metragem válida.'
        : null;
  const category = getCategory(product.categorySlug);
  // Produto sem variantes: foto real do produto (a capa IA só aparece se não houver foto real).
  // A imagem da categoria NUNCA preenche produto sem foto.
  const unitImage = variantless ? resolveProductImage(product) || product.coverImage || null : null;
  const related = getAllProducts().filter(
    (p) => p.categorySlug === product.categorySlug && p.slug !== slug && isProductVisible(p)
  ).slice(0, 4);

  // Pedido sob medida: descrição obrigatória; metragem opcional (vazia = a confirmar).
  const addCustom = () => {
    const note = customNote.trim();
    if (!note) return;
    let customM = null;
    if (customMeters.trim() !== '') {
      customM = parseMeters(customMeters);
      if (customM === null) {
        setCustomError('Informe uma metragem válida ou deixe em branco.');
        return;
      }
    }
    addCustomItem(product, note, customM);
    setCustomNote('');
    setCustomMeters('');
    setCustomError('');
  };

  const stepMeters = (dir) => {
    const base = meters ?? 1;
    const next = Math.min(MAX_M, Math.max(MIN_M, Math.round((base + dir * STEP) * 100) / 100));
    setInputValue(formatBr(next));
  };

  const stepMetragem = multi ? 2 : 1;
  const stepAdicionar = multi ? 3 : 2;

  return (
    <main className="mx-auto max-w-7xl px-4 py-8 md:py-12" data-testid="product-page">
      <nav className="text-xs text-muted-foreground" aria-label="Breadcrumb">
        Início / <Link to={`/categoria/${category.slug}`} className="hover:text-navy">{category.name}</Link> /{' '}
        <span className="text-foreground">{product.name}</span>
      </nav>

      <div className="mt-6 grid gap-8 md:grid-cols-2 md:gap-14">
        <div data-testid="product-gallery">
          <div className="overflow-hidden rounded-2xl bg-muted">
            {variantless ? (
              unitImage ? (
                <div className="relative">
                  <img
                    src={unitImage}
                    alt={product.name}
                    className="aspect-square w-full animate-fade-up object-cover"
                    data-testid="product-main-image"
                  />
                  {isIllustrativeProductImage(product, unitImage) && (
                    <span
                      className="absolute bottom-2 left-2 rounded-md bg-black/55 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white/90"
                      data-testid="product-main-illustrative"
                    >
                      Foto ilustrativa
                    </span>
                  )}
                </div>
              ) : (
                <div
                  className="flex aspect-square w-full flex-col items-center justify-center gap-2 bg-navy-light text-navy/50"
                  data-testid="product-main-photo-pending"
                >
                  <ImageOff className="h-10 w-10" strokeWidth={1.5} />
                  <span className="text-xs font-semibold uppercase tracking-wide">Foto em atualização</span>
                </div>
              )
            ) : coverShown ? (
              <div className="relative">
                <img
                  src={product.coverImage}
                  alt={`${product.name} — foto comercial`}
                  className="aspect-square w-full animate-fade-up object-cover"
                  data-testid="product-cover-image"
                />
                <span
                  className="absolute bottom-2 left-2 rounded-md bg-black/55 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white/90"
                  data-testid="product-cover-illustrative"
                >
                  Foto ilustrativa
                </span>
              </div>
            ) : resolveVariantImage(shown) ? (
              <div className="relative">
                <img
                  key={shown.id}
                  src={resolveVariantImage(shown)}
                  alt={`${product.name} - ${shown.name}`}
                  className="aspect-square w-full animate-fade-up object-cover"
                  data-testid="product-main-image"
                />
                {isIllustrativeVariantImage(shown) && (
                  <span
                    className="absolute bottom-2 left-2 rounded-md bg-black/55 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white/90"
                    data-testid="product-variant-illustrative"
                  >
                    Foto ilustrativa
                  </span>
                )}
              </div>
            ) : (
              <div
                className="flex aspect-square w-full flex-col items-center justify-center gap-2 bg-navy-light text-navy/50"
                data-testid="product-main-photo-pending"
              >
                <ImageOff className="h-10 w-10" strokeWidth={1.5} />
                <span className="text-xs font-semibold uppercase tracking-wide">Foto em atualização</span>
              </div>
            )}
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">{category.name}</p>
          <h1 className="mt-2 font-serif text-3xl tracking-tight text-navy md:text-4xl" data-testid="product-name">
            {product.name}
          </h1>
          {!variantless &&
            (selected ? (
              <>
                <p className="mt-1 text-lg font-medium text-navy" data-testid="product-variant-name">{shown.name}</p>
                {shown.description && (
                  <p className="mt-1 text-sm text-muted-foreground" data-testid="product-variant-description">{shown.description}</p>
                )}
              </>
            ) : (
              <p className="mt-1 text-lg text-muted-foreground" data-testid="product-variant-name">
                {avail.length} opções de cor/estampa disponíveis
              </p>
            ))}

          <p className="mt-4 leading-relaxed text-foreground/80">{product.description}</p>

          {!variantless &&
            (multi ? (
            <section className="mt-6 rounded-2xl border-2 border-navy/15 bg-white p-4" aria-label="Escolha a cor ou estampa" data-testid="product-step-variant">
              <StepTitle n={1} testid="product-step-variant-title">Escolha a cor ou estampa</StepTitle>
              {!selected && (
                <p className="mt-2 flex items-center gap-1.5 text-sm font-medium text-amber-700" data-testid="product-variant-continue-hint">
                  <AlertCircle className="h-4 w-4 shrink-0" /> Escolha uma das opções abaixo para continuar.
                </p>
              )}
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3" role="radiogroup" aria-label="Variantes disponíveis" data-testid="variant-gallery">
                {avail.map((v) => {
                  const isSel = v.id === selected?.id;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => setVariantId(v.id)}
                      role="radio"
                      aria-checked={isSel}
                      data-testid={`variant-option-${v.id}`}
                      className={`relative rounded-xl border-2 p-2 text-left transition-all ${
                        isSel ? 'border-navy shadow-md ring-2 ring-navy/30' : 'border-navy/15 hover:border-navy/50'
                      }`}
                    >
                      {resolveVariantImage(v) ? (
                        <div className="relative">
                          <img
                            src={resolveVariantImage(v)}
                            alt={`${product.name} - ${v.name}`}
                            loading="lazy"
                            className="aspect-square w-full rounded-lg object-cover"
                          />
                          {isIllustrativeVariantImage(v) && (
                            <span
                              className="absolute bottom-1 left-1 rounded bg-black/55 px-1 py-px text-[7px] font-semibold uppercase tracking-wider text-white/90"
                              data-testid={`variant-thumb-illustrative-${v.id}`}
                            >
                              Ilustrativa
                            </span>
                          )}
                        </div>
                      ) : (
                        <div
                          className="flex aspect-square w-full flex-col items-center justify-center gap-1 rounded-lg bg-navy-light text-navy/50"
                          data-testid={`variant-photo-pending-${v.id}`}
                        >
                          <ImageOff className="h-6 w-6" strokeWidth={1.5} />
                          <span className="px-1 text-center text-[10px] font-semibold uppercase tracking-wide">Foto em atualização</span>
                        </div>
                      )}
                      {isSel && (
                        <span
                          className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-navy text-white shadow"
                          data-testid={`variant-selected-check-${v.id}`}
                        >
                          <Check className="h-4 w-4" />
                        </span>
                      )}
                      <span className={`mt-2 block text-center text-xs font-medium leading-tight ${isSel ? 'font-bold text-navy' : 'text-muted-foreground'}`}>
                        {v.name}
                      </span>
                    </button>
                  );
                })}
              </div>
              {selected && (
                <p className="mt-3 flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800" data-testid="product-selected-variant-confirmation">
                  <Check className="h-5 w-5 shrink-0" /> Opção selecionada: {selected.name}
                </p>
              )}
              {selected && isIllustrativeVariantImage(selected) && (
                <p className="mt-2 flex items-center gap-2 rounded-xl bg-navy-light px-4 py-2.5 text-xs leading-relaxed text-muted-foreground" data-testid="product-variant-illustrative-note">
                  <AlertCircle className="h-4 w-4 shrink-0 text-navy/60" />
                  Imagem ilustrativa. Solicite ao vendedor uma foto real do tecido para confirmar cor e aparência.
                </p>
              )}
            </section>
          ) : (
            <p className="mt-6 flex items-center gap-2 rounded-xl bg-navy-light px-4 py-3 text-sm font-semibold text-navy" data-testid="product-single-variant">
              <Check className="h-4 w-4 shrink-0" /> Opção: {avail[0].name}
            </p>
          ))}

          <section className="mt-6" aria-label="Informe a metragem" data-testid="product-step-meters">
            <StepTitle n={stepMetragem} testid="product-step-meters-title">Informe a metragem</StepTitle>
            <label htmlFor="meters-input" className="mt-3 block text-sm font-semibold text-navy">
              Metragem desejada
            </label>
            <div className="mt-2 flex items-center gap-3">
              <button
                type="button"
                onClick={() => stepMeters(-1)}
                aria-label="Diminuir metragem"
                data-testid="product-meters-decrease"
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-navy/20 text-navy hover:bg-navy-light"
              >
                <Minus className="h-4 w-4" />
              </button>
              <div className="flex items-center gap-2">
                <input
                  id="meters-input"
                  data-testid="product-detail-meter-input"
                  inputMode="decimal"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onBlur={() => { if (meters !== null) setInputValue(formatBr(meters)); }}
                  aria-invalid={metersError}
                  className={`h-12 w-24 rounded-xl border bg-white text-center text-xl font-bold outline-none focus:ring-2 ${
                    metersError ? 'border-red-400 focus:border-red-500 focus:ring-red-200' : 'border-input focus:border-navy focus:ring-navy/15'
                  }`}
                  aria-describedby="meters-help"
                />
                <span className="text-sm text-muted-foreground" id="meters-help">metros</span>
              </div>
              <button
                type="button"
                onClick={() => stepMeters(1)}
                aria-label="Aumentar metragem"
                data-testid="product-meters-increase"
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-navy/20 text-navy hover:bg-navy-light"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            {metersError && (
              <p className="mt-2 flex items-center gap-1.5 text-sm font-medium text-red-600" role="alert" data-testid="product-meters-error">
                <AlertCircle className="h-4 w-4 shrink-0" /> Informe uma metragem válida.
              </p>
            )}
            <p className="mt-3 text-xs text-muted-foreground">Atalhos rápidos (opcional — você pode digitar qualquer valor):</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {QUICK_METERS.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setInputValue(formatBr(m))}
                  data-testid={`product-meters-quick-${String(m).replace('.', '-')}`}
                  className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                    meters === m ? 'border-navy bg-navy text-white' : 'border-navy/20 text-foreground/70 hover:border-navy'
                  }`}
                >
                  {formatMeters(m)}
                </button>
              ))}
            </div>
          </section>

          <section className="mt-6" aria-label="Adicionar à solicitação">
            <StepTitle n={stepAdicionar} testid="product-step-add-title">Adicione à solicitação</StepTitle>
            <button
              type="button"
              onClick={() => canAdd && addItem(product, variantless ? null : selected, meters)}
              disabled={!canAdd}
              data-testid="product-detail-add-request-button"
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-semibold tracking-wide text-white transition-colors hover:bg-navy-hover disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ClipboardPlus className="h-5 w-5" />
              ADICIONAR À SOLICITAÇÃO
            </button>
            {disabledReason && (
              <p className="mt-2 flex items-center justify-center gap-1.5 text-center text-sm font-medium text-amber-700" data-testid="product-disabled-reason">
                <AlertCircle className="h-4 w-4 shrink-0" /> {disabledReason}
              </p>
            )}
            {!variantless && !selected && multi && (
              <p className="sr-only" data-testid="product-select-variant-hint">Escolha uma opção acima para adicionar à sua solicitação.</p>
            )}
          </section>

          {!variantless && (
            <section className="mt-6 rounded-2xl border border-dashed border-navy/25 bg-white p-4" aria-label="Não encontrou a cor ou estampa" data-testid="product-custom-request">
              <p className="text-base font-semibold text-navy" data-testid="product-custom-request-title">Não encontrou a cor ou estampa que procura?</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                Descreva a cor, o desenho ou a estampa desejada neste tecido. Sua descrição entra na solicitação junto com o produto e nossa equipe verifica a disponibilidade.
              </p>
              <label htmlFor="custom-note" className="mt-3 block text-sm font-semibold text-navy">O que você procura?</label>
              <textarea
                id="custom-note"
                data-testid="product-custom-note-input"
                value={customNote}
                onChange={(e) => { setCustomNote(e.target.value); setCustomError(''); }}
                rows={2}
                placeholder="Ex.: xadrez vermelho e branco, poá azul-marinho, listrado cinza..."
                className="mt-1.5 w-full rounded-xl border border-input bg-white px-4 py-3 text-sm outline-none focus:border-navy focus:ring-2 focus:ring-navy/15"
              />
              <label htmlFor="custom-meters" className="mt-3 block text-sm font-semibold text-navy">Metragem desejada (opcional)</label>
              <div className="mt-1.5 flex items-center gap-2">
                <input
                  id="custom-meters"
                  data-testid="product-custom-meters-input"
                  inputMode="decimal"
                  value={customMeters}
                  onChange={(e) => { setCustomMeters(e.target.value); setCustomError(''); }}
                  placeholder="Ex.: 2,5"
                  className="h-11 w-28 rounded-xl border border-input bg-white px-3 text-center text-sm font-semibold outline-none focus:border-navy focus:ring-2 focus:ring-navy/15"
                />
                <span className="text-sm text-muted-foreground">metros — pode deixar em branco</span>
              </div>
              {customError && (
                <p className="mt-2 flex items-center gap-1.5 text-sm font-medium text-red-600" role="alert" data-testid="product-custom-error">
                  <AlertCircle className="h-4 w-4 shrink-0" /> {customError}
                </p>
              )}
              <button
                type="button"
                onClick={addCustom}
                disabled={!customNote.trim()}
                data-testid="product-custom-add-button"
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border-2 border-navy px-6 py-3.5 text-sm font-semibold tracking-wide text-navy transition-colors hover:bg-navy-light disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ClipboardPlus className="h-5 w-5" />
                ADICIONAR À SOLICITAÇÃO
              </button>
            </section>
          )}

          <p className="mt-4 flex items-start gap-2 rounded-xl bg-navy-light p-4 text-sm leading-relaxed text-navy" data-testid="product-confirmation-note">
            <MessageCircle className="mt-0.5 h-4 w-4 shrink-0" />
            Disponibilidade, metragem e valores serão confirmados pela nossa equipe pelo WhatsApp.
          </p>

          <div className="mt-6" data-testid="product-tech-data">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Dados técnicos</p>
            <dl className="mt-2 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg bg-navy-light p-3">
                <dt className="text-xs text-muted-foreground">Composição</dt>
                <dd className="mt-0.5 font-medium">{product.composition}</dd>
              </div>
              <div className="rounded-lg bg-navy-light p-3">
                <dt className="text-xs text-muted-foreground">Largura</dt>
                <dd className="mt-0.5 font-medium">{product.width}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-16">
          <h2 className="font-serif text-2xl tracking-tight text-navy">Você também pode gostar</h2>
          <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-4">
            {related.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
